import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useTheme from '../ColorConfig/usetheme';

const BlogDetails = () => {
  const colors = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { post } = location.state || {};
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (!post) {
      navigate('/blog');
    }
  }, [post, navigate]);

  if (!post) {
    return null;
  }

  const handleBackClick = () => {
    navigate(-1);
  };

  // Get all secondary images dynamically
  const getSecondaryImages = () => {
    const images = [];
    
    // Check for secondaryImage1, secondaryImage2, secondaryImage3
    for (let i = 1; i <= 3; i++) {
      const imageKey = `secondaryImage${i}`;
      if (post[imageKey] && post[imageKey].trim() !== '') {
        images.push(`https://api.dremerz.net/uploads/${post[imageKey]}`);
      }
    }
    
    // Also check for secondaryImages field (if it exists and is an array)
    if (post.secondaryImages && Array.isArray(post.secondaryImages)) {
      post.secondaryImages.forEach(img => {
        if (img && img.trim() !== '') {
          images.push(`https://api.dremerz.net/uploads/${img}`);
        }
      });
    }
    
    return images;
  };

  const secondaryImages = getSecondaryImages();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ 
        minHeight: '100vh', 
        backgroundColor: colors.bodyBlack,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        color: colors.white
      }}
    >
      {/* Back Button and Date Row */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: isMobile ? '15px' : '20px'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '15px' : '20px'
        }}>
          <button
            onClick={handleBackClick}
            style={{
              backgroundColor: 'transparent',
              color: colors.white,
              border: 'none',
              padding: isMobile ? '8px 12px' : '10px 16px',
              borderRadius: '8px',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = colors.hoverBlack;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
            </svg>
            {!isMobile && 'Back'}
          </button>
          
          <div style={{
            color: colors.white,
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h6V.5a.5.5 0 0 1 1 0V1h1.5A1.5 1.5 0 0 1 14 2.5v11a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5v-11A1.5 1.5 0 0 1 3.5 1H5V.5a.5.5 0 0 1 .5-.5zM3 2.5v1h10v-1a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5zM3 4.5v9a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-9H3z"/>
            </svg>
            {post.date}
          </div>
        </div>
      </motion.div>

      {/* Blog Content */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          padding: isMobile ? '0 15px 40px 15px' : '0 20px 60px 20px'
        }}
      >
        {/* Category */}
        <motion.div variants={itemVariants} style={{
          marginBottom: isMobile ? '15px' : '20px',
        }}>
          <div style={{
            backgroundColor: `var(--color-primary)`,
            color: colors.white,
            padding: isMobile ? '6px 12px' : '8px 16px',
            borderRadius: '20px',
            fontSize: isMobile ? '0.8rem' : '0.9rem',
            fontWeight: '600',
            display: 'inline-block'
          }}>
            {post.category}
          </div>
        </motion.div>

        {/* Title */}
        <motion.h3 
          variants={itemVariants}
          style={{ 
            fontSize: isMobile ? '1.3rem' : '1.5rem',
            fontWeight: '600',
            margin: '0 0 20px 0',
            lineHeight: '1.4',
            color: colors.white
          }}
        >
          {post.title}
        </motion.h3>

        {/* Featured Image */}
        <motion.div 
          variants={itemVariants}
          style={{
            width: '100%',
            height: isMobile ? '250px' : '400px',
            borderRadius: isMobile ? '12px' : '16px',
            overflow: 'hidden',
            marginBottom: isMobile ? '25px' : '32px'
          }}
        >
          <img 
            src={post.image} 
            alt={post.title}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover'
            }}
          />
        </motion.div>

        {/* Excerpt */}
        <motion.div 
          variants={itemVariants}
          style={{
            padding: isMobile ? '18px' : '24px',
            backgroundColor: colors.hoverBlack,
            borderRadius: isMobile ? '10px' : '12px',
            marginBottom: isMobile ? '25px' : '32px',
            borderLeft: `4px solid var(--color-primary)`
          }}
        >
          <h5 style={{
            fontSize: isMobile ? '1rem' : '1.1rem',
            lineHeight: isMobile ? '1.6' : '1.6',
            color: colors.gray,
            margin: 0,
            fontStyle: 'italic'
          }}>
            {post.excerpt}
          </h5>
        </motion.div>

        {/* Full Content */}
      <motion.div 
  variants={itemVariants}
  style={{
    marginBottom: secondaryImages.length > 0 ? (isMobile ? '30px' : '40px') : '0'
  }}
>
  <h6 style={{
    fontSize: isMobile ? '1rem' : '1.1rem',
    lineHeight: isMobile ? '1.7' : '1.8',
    color: colors.white
  }}>
    {post.fullContent}
  </h6>
</motion.div>

        {/* Secondary Images Grid */}
        {secondaryImages.length > 0 && (
          <motion.div
            variants={itemVariants}
            style={{
              marginTop: isMobile ? '30px' : '40px'
            }}
          >
            {/* <h4 style={{
              fontSize: isMobile ? '1.1rem' : '1.3rem',
              fontWeight: '600',
              marginBottom: isMobile ? '20px' : '24px',
              color: colors.white
            }}>
              Gallery
            </h4> */}
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: isMobile ? '15px' : '20px'
            }}>
              {secondaryImages.map((imgUrl, index) => (
                <motion.div
                  key={index}
                  variants={imageVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                  style={{
                    width: '100%',
                    height: isMobile ? '200px' : '250px',
                    borderRadius: isMobile ? '10px' : '12px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                  onClick={() => {
                    // Optional: Add lightbox functionality here
                    window.open(imgUrl, '_blank');
                  }}
                >
                  <img
                    src={imgUrl}
                    alt={`${post.title} - Image ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Responsive Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            /* Ensure single column on mobile */
            .secondary-images-grid {
              grid-template-columns: 1fr !important;
            }
          }
          
          @media (max-width: 480px) {
            /* Extra small screens adjustments */
            .secondary-images-grid img {
              height: 180px !important;
            }
          }
        `}
      </style>
    </motion.div>
  );
};

export default BlogDetails;